import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { formatDay } from '../../utils/date';
import { EntityType, IRange } from '../types';
import { aggregatesProvider, IAggregates } from './aggregatesProvider';
import {
  IExclusiveDateOption,
  IInclusiveDateOption,
} from './settings/dates/dateRangeProvider';

export interface ICrossTestAggregates {
  [queryHash: string]: IAggregates;
}

export const crossTestAggregates = (
  queryHashes: string[],
  entityId: string,
  entityType: EntityType,
  dateType: IInclusiveDateOption | IExclusiveDateOption,
  range: IRange,
  afterData?: ICrossTestAggregates
): Observable<ICrossTestAggregates> => {
  /// use a date range of all time if no range is provided
  const mRange: IRange =
    range == null
      ? {
          from: { value: 0, label: 'all-time' },
          to: {
            value: dateType?.value?.includes('previous')
              ? dayjs(new Date()).startOf('day').valueOf()
              : dayjs(new Date()).endOf('day').valueOf(),
            label: formatDay(dayjs(new Date()).startOf('day').valueOf()),
          },
        }
      : range;
  /// create a list of all the necessary observables
  const obs: Array<Observable<ICrossTestAggregates | Record<string, unknown>>> =
    queryHashes.map((queryHash: string): Observable<ICrossTestAggregates> => {
      /// if they have all-time selected, ignore afterSessionId
      const afterSessionId =
        dateType.value !== 'all-time' && afterData !== null
          ? getAfterSessionId(queryHash, afterData)
          : null;

      return aggregatesProvider(
        entityId,
        entityType,
        queryHash,
        dateType,
        mRange,
        afterSessionId
      ).pipe(
        map(
          (agg: IAggregates): ICrossTestAggregates => ({
            [queryHash]: agg,
          })
        )
      );
    });
  if (obs.length === 0) return of({});

  /// accumulate the results of all the observables
  return combineLatest(obs).pipe(
    map((args: ICrossTestAggregates[]): ICrossTestAggregates => {
      return args.reduce(
        (
          acc: ICrossTestAggregates,
          curr: ICrossTestAggregates
        ): ICrossTestAggregates => {
          return { ...acc, ...curr };
        },
        {}
      );
    })
  );
};

/**
 * @description gets the session id for the last session in the data set
 * @param queryHash
 * @param data
 * @returns
 */
const getAfterSessionId = (
  queryHash: string,
  data: ICrossTestAggregates
): string => {
  if (isEmpty(data)) return null;
  const aggregates = data?.[queryHash];
  if (isEmpty(aggregates)) return null;
  const lastSession = aggregates.sessions?.[aggregates.sessions.length - 1];
  if (isEmpty(lastSession)) return null;
  return lastSession.meta.id;
};
